import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { OFXFile, BankStatement, BankTransaction } from '../../types/bank-reconciliation';
import { BankStatementProcessor } from '../../services/reconciliation/BankStatementProcessor';
import { useAuth } from '@/hooks/useAuth';
import { useSmartAccountDefaults } from '@/hooks/useSmartAccountDefaults';

interface SimplifiedImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (result: {
    success: boolean;
    file: File;
    bankCode: string;
    totalTransactions: number;
    statement?: BankStatement;
    transactions?: BankTransaction[];
  }) => void;
}

interface ProcessingState {
  isProcessing: boolean;
  stage: 'uploading' | 'parsing' | 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
  error?: string;
  result?: {
    statement: BankStatement;
    transactions: BankTransaction[];
  };
}

export function SimplifiedImportDialog({ open, onOpenChange, onSuccess }: SimplifiedImportDialogProps) {
  const { user } = useAuth();
  const { getDefaultAccount } = useSmartAccountDefaults();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingState, setProcessingState] = useState<ProcessingState>({
    isProcessing: false,
    stage: 'uploading',
    progress: 0,
    message: ''
  });

  // File validation
  const validateFile = (file: File): boolean => {
    const validExtensions = ['.ofx'];
    const validMimeTypes = ['application/ofx', 'application/x-ofx', 'text/xml'];

    const hasValidExtension = validExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    const hasValidMimeType = validMimeTypes.includes(file.type) ||
                            file.type === '' || // Some browsers don't detect OFX MIME type
                            file.name.toLowerCase().endsWith('.ofx');

    const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit

    if (!hasValidExtension) {
      toast.error('Formato de arquivo inválido. Por favor, selecione um arquivo .ofx');
      return false;
    }

    if (!isValidSize) {
      toast.error('Arquivo muito grande. O tamanho máximo é 50MB');
      return false;
    }

    return true;
  };

  // File selection handlers
  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      setProcessingState({
        isProcessing: false,
        stage: 'uploading',
        progress: 0,
        message: ''
      });
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const ofxFile = files.find(file =>
      file.name.toLowerCase().endsWith('.ofx')
    );

    if (ofxFile) {
      handleFileSelect(ofxFile);
    } else {
      toast.error('Por favor, selecione um arquivo .ofx válido');
    }
  }, []);

  // Processing logic
  const processFile = async () => {
    if (!selectedFile || !user) return;

    setProcessingState({
      isProcessing: true,
      stage: 'uploading',
      progress: 0,
      message: 'Enviando arquivo...'
    });

    try {
      // Get default payment account for bank statements
      const defaultAccount = await getDefaultAccount('expense');
      if (!defaultAccount) {
        throw new Error('Nenhuma conta de pagamento encontrada. Configure uma conta padrão nas configurações.');
      }

      // Stage 1: Upload
      setProcessingState(prev => ({
        ...prev,
        stage: 'parsing',
        progress: 20,
        message: 'Analisando arquivo OFX...'
      }));

      toast.loading('Processando arquivo OFX...');

      // Stage 2: Parse and process
      const processingResult = await BankStatementProcessor.processStatement({
        file: selectedFile,
        bankCode: '', // Will be auto-detected
        paymentAccountId: defaultAccount.toString(),
        userId: user.id
      });

      setProcessingState(prev => ({
        ...prev,
        stage: 'processing',
        progress: 60,
        message: 'Processando transações...'
      }));

      if (!processingResult.success) {
        throw new Error(processingResult.error || 'Erro ao processar arquivo');
      }

      // Stage 3: Load transactions
      setProcessingState(prev => ({
        ...prev,
        stage: 'processing',
        progress: 80,
        message: 'Carregando transações...'
      }));

      const transactions = await BankStatementProcessor.getBankTransactions(
        processingResult.statement.id,
        user.id,
        { limit: 100 }
      );

      // Stage 4: Complete
      setProcessingState({
        isProcessing: false,
        stage: 'completed',
        progress: 100,
        message: `Sucesso! ${transactions.length} transações processadas`,
        result: {
          statement: processingResult.statement,
          transactions
        }
      });

      toast.success(`Arquivo processado com sucesso! ${transactions.length} transações encontradas.`);

    } catch (error) {
      console.error('Error processing file:', error);

      setProcessingState({
        isProcessing: false,
        stage: 'error',
        progress: 0,
        message: '',
        error: error instanceof Error ? error.message : 'Erro ao processar arquivo'
      });

      toast.error('Erro ao processar arquivo. Por favor, tente novamente.');
    } finally {
      toast.dismiss();
    }
  };

  // Success callback
  const handleSuccess = () => {
    if (processingState.result && selectedFile) {
      onSuccess({
        success: true,
        file: selectedFile,
        bankCode: processingState.result.statement.bank_code || '',
        totalTransactions: processingState.result.transactions.length,
        statement: processingState.result.statement,
        transactions: processingState.result.transactions
      });

      // Reset dialog
      setSelectedFile(null);
      setProcessingState({
        isProcessing: false,
        stage: 'uploading',
        progress: 0,
        message: ''
      });

      onOpenChange(false);
    }
  };

  // Reset handlers
  const handleReset = () => {
    setSelectedFile(null);
    setProcessingState({
      isProcessing: false,
      stage: 'uploading',
      progress: 0,
      message: ''
    });
  };

  const handleClose = () => {
    if (!processingState.isProcessing) {
      handleReset();
      onOpenChange(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Extrato Bancário
            </CardTitle>
            <CardDescription>
              Envie seu arquivo .ofx para conciliação automática
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={processingState.isProcessing}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Upload Area */}
          {!selectedFile && !processingState.isProcessing && (
            <div
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".ofx"
                onChange={handleFileInputChange}
                className="hidden"
              />

              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>

                <div>
                  <h3 className="font-medium text-lg">
                    Arraste e solte seu arquivo .ofx aqui
                  </h3>
                  <p className="text-muted-foreground">
                    ou clique para selecionar um arquivo
                  </p>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p>Tamanho máximo: 50MB</p>
                  <p>Formatos aceitos: .ofx</p>
                </div>
              </div>
            </div>
          )}

          {/* Selected File Info */}
          {selectedFile && processingState.stage === 'uploading' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  disabled={processingState.isProcessing}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={processFile}
                  disabled={processingState.isProcessing}
                  className="min-w-[200px]"
                >
                  Processar Arquivo
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Processing State */}
          {processingState.isProcessing && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 mb-2">
                  <Badge variant={processingState.stage === 'error' ? 'destructive' : 'default'}>
                    {processingState.stage === 'uploading' && 'Enviando'}
                    {processingState.stage === 'parsing' && 'Analisando'}
                    {processingState.stage === 'processing' && 'Processando'}
                    {processingState.stage === 'completed' && 'Concluído'}
                    {processingState.stage === 'error' && 'Erro'}
                  </Badge>
                </div>

                <h3 className="font-medium text-lg">
                  {processingState.message}
                </h3>
              </div>

              <Progress value={processingState.progress} className="w-full" />

              {processingState.stage === 'error' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {processingState.error}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Completed State */}
          {processingState.stage === 'completed' && processingState.result && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Arquivo processado com sucesso!
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {processingState.result.transactions.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Transações</p>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {processingState.result.statement.bank_code || 'Auto'}
                  </p>
                  <p className="text-sm text-muted-foreground">Banco</p>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {processingState.result.statement.account_number || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">Conta</p>
                </div>
              </div>

              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                >
                  Importar outro arquivo
                </Button>
                <Button onClick={handleSuccess}>
                  Continuar para Conciliação
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}